import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const before = quantityInput.value;
    quantityInput.stepUp();

    // stepUp() respects the input's `max` attribute; if value didn't change
    // and a max purchase limit is configured, the user attempted to exceed it.
    if (quantityInput.value === before && this.#dispatchPurchaseLimitReachedIfApplicable()) {
      return;
    }

    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    const attempted = parseInt(quantityInput.value || '0', 10);
    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    // Detect when typed/programmatic value got clamped DOWN to max (max purchase limit case).
    if (!Number.isNaN(attempted) && attempted > newValue) {
      this.#dispatchPurchaseLimitReachedIfApplicable();
    }

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Dispatches a `quantity-selector:purchase-limit-reached` event when the
   * selector has max-purchase-limit metadata and the current cart line is known.
   * @returns {boolean} Whether the event was dispatched.
   */
  #dispatchPurchaseLimitReachedIfApplicable() {
    const { quantityInput } = this.refs;
    const message = this.dataset.maxPurchaseMessage;
    const cartLine = Number(quantityInput.dataset.cartLine);

    if (!this.dataset.maxPurchaseLimit || !message || !cartLine) return false;

    this.dispatchEvent(
      new CustomEvent('quantity-selector:purchase-limit-reached', {
        bubbles: true,
        detail: { cartLine, message },
      })
    );
    return true;
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
